import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Key,
  Check,
  ShieldCheck,
  FolderOpen,
  X,
  Loader2,
  Github,
  GitBranch,
  Laptop,
  ExternalLink,
} from 'lucide-react';
import {
  addCodebase,
  addCodebaseBatch,
  listProjectSources,
  type CodebaseResponse,
  type DetectedEnvKey,
  type AddCodebaseResult,
  type AddCodebaseInput,
  type EnvLeakResponse,
  type ProjectSourceCatalogEntry,
  type ProjectSourceRecord,
} from '@/lib/api';
import { DirectoryBrowser } from './DirectoryBrowser';
import { ConsumeProjectWizard } from './ConsumeProjectWizard';
import { useRegistrationStore } from '@/stores/registration-store';
import { useProject } from '@/contexts/ProjectContext';
import { getProjectDisplayName } from '@/lib/repo-label';

interface DetectedKeyState {
  name: string;
  file: string;
  moveToSecure: boolean;
}

type ProjectInputMode = 'internal' | 'github' | 'gitea' | 'local-device' | 'path';

const inputHelpByMode: Record<
  ProjectInputMode,
  { title: string; description: string; example?: string }
> = {
  internal: {
    title: 'Internal Gitea baseline',
    description: 'Start from a published Maestro baseline with source provenance preserved.',
  },
  github: {
    title: 'GitHub repository',
    description: 'Clone a GitHub repo into Maestro cloud workspaces and register it for workflows.',
    example: 'https://github.com/org/repo.git',
  },
  gitea: {
    title: 'Gitea repository',
    description: 'Clone a Mahoosuc or customer Gitea repo using the cloud deployment’s git access.',
    example: 'ssh://git@gitea.example.com:2222/org/repo.git',
  },
  'local-device': {
    title: 'Local device checkout',
    description:
      'For cloud Maestro, enroll the workstation first so the cloud control plane can safely reach local projects without pretending the server can browse your laptop.',
  },
  path: {
    title: 'Server-mounted local path',
    description:
      'Register a git checkout already mounted on the Maestro server or paired WSL host.',
    example: '/mnt/wsl/PHYSICALDRIVE1/dev/projects/project-name',
  },
};

function receiptProviderLabel(provider: string | null | undefined): string {
  if (!provider) return 'Not reported';
  if (provider === 'github') return 'GitHub';
  if (provider === 'gitea') return 'Gitea';
  if (provider === 'filesystem') return 'Server/local path';
  if (provider === 'git') return 'Git URL';
  return provider;
}

function shortenId(id: string | null | undefined): string {
  if (!id) return 'Not reported';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

/**
 * Map a catalog entry to the `projectSource` payload for /api/codebases.
 * Pure + exported for unit testing (no React/component state).
 */
export function buildProjectSourceRecord(source: ProjectSourceCatalogEntry): ProjectSourceRecord {
  return {
    kind: source.kind,
    provider: source.provider,
    baselineId: source.baselineId ?? source.id,
    repositoryUrl: source.repositoryUrl,
    localMirrorPath: source.localMirrorPath,
    ref: source.defaultRef ?? source.ref,
    seedRevision: typeof source.seedRevision === 'string' ? source.seedRevision : undefined,
    visibility: source.visibility,
  };
}

/**
 * Build the POST /api/codebases body for the active source mode, or null when
 * the form is not submittable (empty url/path, or local-device which enrolls
 * out-of-band). Pure + exported so the per-mode payload shape — especially the
 * `provider` slug (github/gitea) and the always-on `createProject` — is unit
 * tested without rendering the dialog.
 */
export function buildAddInput(params: {
  sourceMode: ProjectInputMode;
  pathInput: string;
  urlInput: string;
  selectedSource: ProjectSourceCatalogEntry | null;
}): AddCodebaseInput | null {
  const { sourceMode, pathInput, urlInput, selectedSource } = params;

  // local-device enrolls a workstation checkout out-of-band (the submit button
  // is also disabled via canSubmit) — never a /api/codebases submission.
  if (sourceMode === 'local-device') {
    return null;
  }

  if (sourceMode === 'path') {
    const trimmed = pathInput.trim();
    return trimmed ? { path: trimmed, createProject: true } : null;
  }

  if (sourceMode === 'github' || sourceMode === 'gitea') {
    const trimmed = urlInput.trim();
    return trimmed
      ? {
          url: trimmed,
          createProject: true,
          projectSource: {
            kind: 'git_url',
            provider: sourceMode,
            repositoryUrl: trimmed,
            visibility: 'internal',
          },
        }
      : null;
  }

  if (!selectedSource?.repositoryUrl || selectedSource.registered) {
    return null;
  }

  return {
    url: selectedSource.repositoryUrl,
    createProject: true,
    projectSource: buildProjectSourceRecord(selectedSource),
  };
}

export function RegistrationReceipt({
  codebase,
}: {
  codebase: CodebaseResponse;
}): React.ReactElement {
  const receipt = codebase.registration_receipt;
  const source = receipt?.repository_url ?? codebase.repository_url ?? codebase.default_cwd;
  const rows = [
    [
      'Provider',
      receiptProviderLabel(receipt?.source_provider ?? codebase.project_source?.provider),
    ],
    ['Source', source],
    ['Codebase ID', shortenId(receipt?.codebase_id ?? codebase.id)],
    ['Project code', receipt?.project_code ?? 'Not reported'],
    ['Project ID', shortenId(receipt?.project_id)],
    ['Project link', receipt?.project_link_role ?? 'Not reported'],
  ] as const;

  return (
    <section className="w-full rounded-lg border border-border bg-surface-muted/40 p-3 text-left">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-text-primary">Registration receipt</h3>
          <p className="mt-0.5 text-xs text-text-tertiary">
            Registered source is ready for lifecycle consumption; it is not consumed until you run
            the next step.
          </p>
        </div>
        {receipt && (
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline">
              {receipt.project_created ? 'Project created' : 'Existing project linked'}
            </Badge>
            <Badge variant="outline">
              {receipt.codebase_already_existed ? 'Existing codebase' : 'New codebase'}
            </Badge>
          </div>
        )}
      </div>
      <dl className="mt-3 grid gap-2 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="min-w-0 rounded-md border border-border/70 bg-surface px-2.5 py-2"
          >
            <dt className="text-[10px] uppercase tracking-wide text-text-tertiary">{label}</dt>
            <dd className="mt-0.5 truncate font-mono text-xs text-text-primary" title={value}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {!receipt && (
        <p className="mt-2 text-xs text-warning">
          Project link metadata was not returned by the API; verify `/api/projects` before treating
          this registration as development-ready.
        </p>
      )}
    </section>
  );
}

type DialogStep =
  | { kind: 'input' }
  | { kind: 'browse' }
  | { kind: 'batch-confirm'; paths: string[] }
  | { kind: 'key-review'; request: AddCodebaseInput; detectedKeys: DetectedKeyState[] }
  | { kind: 'success'; codebase: CodebaseResponse }
  | { kind: 'batch-started'; batchId: string; total: number };

interface AddProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (codebase: CodebaseResponse) => void;
}

export function AddProjectDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddProjectDialogProps): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setSelectedProjectId } = useProject();
  const [step, setStep] = useState<DialogStep>({ kind: 'input' });
  const [consumeWizardOpen, setConsumeWizardOpen] = useState(false);
  const [registeredCodebase, setRegisteredCodebase] = useState<CodebaseResponse | null>(null);
  const [consumeTarget, setConsumeTarget] = useState<{ id: string; name: string } | null>(null);
  const [sourceMode, setSourceMode] = useState<ProjectInputMode>('internal');
  const [pathInput, setPathInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const startBatch = useRegistrationStore(state => state.startBatch);
  const projectSourcesQuery = useQuery({
    queryKey: ['projectSources'],
    queryFn: listProjectSources,
    enabled: open,
  });

  const projectSources = projectSourcesQuery.data?.sources ?? [];
  const unregisteredProjectSources = projectSources.filter(source => !source.registered);
  const recommendedUnregisteredSource =
    unregisteredProjectSources.find(source => source.recommended) ??
    unregisteredProjectSources[0] ??
    null;
  const selectedSource =
    projectSources.find(source => source.id === selectedSourceId) ??
    recommendedUnregisteredSource ??
    projectSources[0] ??
    null;
  const allProjectSourcesRegistered =
    projectSources.length > 0 && projectSources.every(source => source.registered);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setStep({ kind: 'input' });
      setSourceMode('internal');
      setPathInput('');
      setUrlInput('');
      setSelectedSourceId('');
      setError(null);
      setConsumeTarget(null);
    }
  }, [open]);

  useEffect(() => {
    if (recommendedUnregisteredSource && selectedSourceId !== recommendedUnregisteredSource.id) {
      const selected = projectSources.find(source => source.id === selectedSourceId);
      if (!selected || selected.registered) {
        setSelectedSourceId(recommendedUnregisteredSource.id);
      }
    } else if (!selectedSourceId && selectedSource) {
      setSelectedSourceId(selectedSource.id);
    }
  }, [projectSources, recommendedUnregisteredSource, selectedSource, selectedSourceId]);

  // Auto-close after batch starts (info panel shows progress)
  useEffect((): (() => void) | undefined => {
    if (step.kind !== 'batch-started') return;
    const timer = setTimeout(() => {
      onOpenChange(false);
    }, 1000);
    return (): void => {
      clearTimeout(timer);
    };
  }, [step, onOpenChange]);

  const addMutation = useMutation({
    mutationFn: (input: AddCodebaseInput) => addCodebase(input),
    onSuccess: (result: AddCodebaseResult) => {
      refreshProjectReadinessQueries();
      setRegisteredCodebase(result);
      setStep({ kind: 'success', codebase: result });
      onSuccess(result);
    },
    onError: (err: Error, input: AddCodebaseInput) => {
      const status = (err as Error & { status?: number }).status;
      if (status === 422) {
        try {
          const body = JSON.parse(
            err.message.replace(/^API error 422(?: \([^)]+\))?:\s*/, '')
          ) as EnvLeakResponse;
          if (body.canMigrate && body.detectedKeys?.length > 0) {
            setStep({
              kind: 'key-review',
              request: input,
              detectedKeys: body.detectedKeys.map((k: DetectedEnvKey) => ({
                name: k.name,
                file: k.file,
                moveToSecure: true,
              })),
            });
            setError(null);
            return;
          }
          // Parsed but not a migratable env-leak — fall through to raw error.
        } catch {
          // 422 body not parseable as an env-leak response — surface the raw
          // message explicitly rather than relying on fall-through.
          setError(err.message);
          return;
        }
      }
      setError(err.message);
    },
  });

  const migrateMutation = useMutation({
    mutationFn: (input: AddCodebaseInput) =>
      addCodebase({
        ...input,
        migrateKeys: true,
      }),
    onSuccess: (result: AddCodebaseResult) => {
      refreshProjectReadinessQueries();
      void queryClient.invalidateQueries({ queryKey: ['secureKeys'] });
      setRegisteredCodebase(result);
      setStep({ kind: 'success', codebase: result });
      onSuccess(result);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const batchMutation = useMutation({
    mutationFn: (input: { paths: string[]; migrateKeys?: boolean }) =>
      addCodebaseBatch(input.paths, input.migrateKeys),
    onSuccess: result => {
      startBatch(result.batchId, step.kind === 'batch-confirm' ? step.paths : []);
      setStep({ kind: 'batch-started', batchId: result.batchId, total: result.total });
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  function handleAddSubmit(e: React.FormEvent): void {
    e.preventDefault();
    const input = buildAddInput({ sourceMode, pathInput, urlInput, selectedSource });
    if (!input) return;
    setError(null);
    addMutation.mutate(input);
  }

  function refreshProjectReadinessQueries(): void {
    void queryClient.invalidateQueries({ queryKey: ['codebases'] });
    void queryClient.invalidateQueries({ queryKey: ['projectSources'] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['projects-health'] });
  }

  function handleOpenWorkbench(codebase: CodebaseResponse): void {
    setSelectedProjectId(codebase.id);
    refreshProjectReadinessQueries();
    onOpenChange(false);
    void navigate('/workbench');
  }

  function handleOpenConsume(codebase: CodebaseResponse): void {
    setSelectedProjectId(codebase.id);
    refreshProjectReadinessQueries();
    setConsumeTarget({
      id: codebase.id,
      name: getProjectDisplayName({
        name: codebase.name,
        repository_url: codebase.repository_url,
      }),
    });
    onOpenChange(false);
    setConsumeWizardOpen(true);
  }

  function handleOpenRegisteredSource(source: ProjectSourceCatalogEntry): void {
    if (!source.registeredCodebaseId) return;
    setSelectedProjectId(source.registeredCodebaseId);
    refreshProjectReadinessQueries();
    onOpenChange(false);
    void navigate('/workbench');
  }

  function handleConsumeRegisteredSource(source: ProjectSourceCatalogEntry): void {
    if (!source.registeredCodebaseId) return;
    setSelectedProjectId(source.registeredCodebaseId);
    setConsumeTarget({ id: source.registeredCodebaseId, name: source.label });
    refreshProjectReadinessQueries();
    onOpenChange(false);
    setConsumeWizardOpen(true);
  }

  function handleBrowseSelect(paths: string[]): void {
    if (paths.length === 1) {
      setPathInput(paths[0]);
      setStep({ kind: 'input' });
    } else if (paths.length > 1) {
      setStep({ kind: 'batch-confirm', paths });
    }
  }

  function handleBatchSubmit(migrateKeys?: boolean): void {
    if (step.kind !== 'batch-confirm') return;
    setError(null);
    batchMutation.mutate({ paths: step.paths, migrateKeys });
  }

  function removeBatchPath(index: number): void {
    if (step.kind !== 'batch-confirm') return;
    const updated = step.paths.filter((_, i) => i !== index);
    if (updated.length === 0) {
      setStep({ kind: 'input' });
    } else {
      setStep({ ...step, paths: updated });
    }
  }

  function handleMigrate(): void {
    if (step.kind !== 'key-review') return;
    setError(null);
    migrateMutation.mutate(step.request);
  }

  function handleSkipMigration(): void {
    if (step.kind !== 'key-review') return;
    setError(null);
    addMutation.mutate(step.request);
  }

  function toggleKey(index: number): void {
    if (step.kind !== 'key-review') return;
    const updated = [...step.detectedKeys];
    updated[index] = { ...updated[index], moveToSecure: !updated[index].moveToSecure };
    setStep({ ...step, detectedKeys: updated });
  }

  const isPending = addMutation.isPending || migrateMutation.isPending || batchMutation.isPending;
  const displayError =
    error ??
    (sourceMode === 'internal' && projectSourcesQuery.error instanceof Error
      ? projectSourcesQuery.error.message
      : null);
  const canSubmit =
    sourceMode === 'path'
      ? Boolean(pathInput.trim())
      : sourceMode === 'github' || sourceMode === 'gitea'
        ? Boolean(urlInput.trim())
        : sourceMode === 'local-device'
          ? false
          : Boolean(selectedSource?.repositoryUrl) &&
            !selectedSource?.registered &&
            !projectSourcesQuery.isLoading;

  const modeHelp = inputHelpByMode[sourceMode];

  return (
    <>
      <Dialog open={open && step.kind !== 'browse'} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          {step.kind === 'input' && (
            <>
              <DialogHeader>
                <DialogTitle>Add a development-ready project</DialogTitle>
                <DialogDescription>
                  Register source from GitHub, Gitea, or a local device, then consume it into the
                  delivery lifecycle when the codebase is ready.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSubmit} className="space-y-3">
                <div className="rounded-lg border border-border bg-surface-muted/40 p-3">
                  <div className="flex items-start gap-3">
                    {sourceMode === 'github' ? (
                      <Github className="mt-0.5 h-4 w-4 text-text-secondary" />
                    ) : sourceMode === 'gitea' || sourceMode === 'internal' ? (
                      <GitBranch className="mt-0.5 h-4 w-4 text-text-secondary" />
                    ) : (
                      <Laptop className="mt-0.5 h-4 w-4 text-text-secondary" />
                    )}
                    <div className="min-w-0 space-y-1">
                      <div className="text-sm font-medium text-text-primary">{modeHelp.title}</div>
                      <p className="text-xs leading-5 text-text-secondary">
                        {modeHelp.description}
                      </p>
                      {modeHelp.example && (
                        <div className="truncate font-mono text-[11px] text-text-tertiary">
                          Example: {modeHelp.example}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <Tabs
                  value={sourceMode}
                  onValueChange={value => {
                    setSourceMode(value as ProjectInputMode);
                    setError(null);
                  }}
                  className="space-y-3"
                >
                  <TabsList className="grid h-auto w-full grid-cols-2 md:grid-cols-5">
                    <TabsTrigger value="internal" className="min-h-11">
                      Internal
                    </TabsTrigger>
                    <TabsTrigger value="github" className="min-h-11">
                      GitHub
                    </TabsTrigger>
                    <TabsTrigger value="gitea" className="min-h-11">
                      Gitea
                    </TabsTrigger>
                    <TabsTrigger value="local-device" className="min-h-11">
                      Local device
                    </TabsTrigger>
                    <TabsTrigger
                      value="path"
                      className="min-h-11"
                      data-testid="add-project-path-tab"
                    >
                      Path
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="internal" className="space-y-3">
                    {projectSourcesQuery.isLoading ? (
                      <div className="rounded-md border border-border px-3 py-4 text-sm text-text-secondary">
                        Loading internal project sources...
                      </div>
                    ) : projectSources.length === 0 ? (
                      <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-4 text-sm text-warning">
                        No internal project sources are published yet.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {allProjectSourcesRegistered && (
                          <div className="rounded-md border border-border bg-surface-muted/40 px-3 py-3 text-sm text-text-secondary">
                            No unregistered approved internal project sources remain. Use GitHub,
                            Gitea, or Path to add another source.
                          </div>
                        )}
                        {projectSources.map(source => {
                          const isSelected = selectedSource?.id === source.id;
                          return (
                            <button
                              key={source.id}
                              type="button"
                              onClick={() => {
                                setSelectedSourceId(source.id);
                              }}
                              className={`w-full rounded-md border p-3 text-left transition-colors ${
                                source.registered
                                  ? 'border-border bg-surface-muted/30 opacity-80'
                                  : isSelected
                                    ? 'border-primary bg-primary/5'
                                    : 'border-border hover:bg-surface'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium text-text-primary">
                                    {source.label}
                                  </div>
                                  <div className="text-sm text-text-secondary">
                                    {source.description}
                                  </div>
                                  <div className="mt-1 truncate font-mono text-[10px] text-text-tertiary">
                                    {source.repositoryUrl}
                                  </div>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  {source.registered ? (
                                    <Badge variant="outline">Already registered</Badge>
                                  ) : source.recommended ? (
                                    <Badge variant="outline">Recommended</Badge>
                                  ) : null}
                                  {source.registeredDefaultCwd && (
                                    <span
                                      className="max-w-40 truncate text-[10px] text-text-tertiary"
                                      title={source.registeredDefaultCwd}
                                    >
                                      {source.registeredDefaultCwd}
                                    </span>
                                  )}
                                  {(source.defaultRef ?? source.ref) && (
                                    <span className="text-[10px] text-text-tertiary">
                                      ref {source.defaultRef ?? source.ref}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {source.registered && source.registeredCodebaseId && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={event => {
                                      event.stopPropagation();
                                      handleOpenRegisteredSource(source);
                                    }}
                                  >
                                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                    Open
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={event => {
                                      event.stopPropagation();
                                      handleConsumeRegisteredSource(source);
                                    }}
                                  >
                                    Consume lifecycle
                                  </Button>
                                </div>
                              )}
                            </button>
                          );
                        })}
                        {selectedSource?.localMirrorPath && (
                          <div className="text-[10px] text-text-tertiary">
                            Local mirror available at {selectedSource.localMirrorPath}
                          </div>
                        )}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="github" className="space-y-3">
                    <label
                      htmlFor="project-github-url"
                      className="text-sm font-medium text-text-primary"
                    >
                      GitHub repository URL
                    </label>
                    <Input
                      id="project-github-url"
                      value={urlInput}
                      onChange={e => {
                        setUrlInput(e.target.value);
                      }}
                      placeholder="https://github.com/org/repo.git"
                      autoFocus
                      disabled={isPending}
                    />
                    <p className="text-xs text-text-tertiary">
                      Private repositories require the cloud Maestro runtime to have GitHub clone
                      credentials configured; tokens are never typed into this dialog.
                    </p>
                  </TabsContent>

                  <TabsContent value="gitea" className="space-y-3">
                    <label
                      htmlFor="project-gitea-url"
                      className="text-sm font-medium text-text-primary"
                    >
                      Gitea repository URL
                    </label>
                    <Input
                      id="project-gitea-url"
                      value={urlInput}
                      onChange={e => {
                        setUrlInput(e.target.value);
                      }}
                      placeholder="ssh://git@gitea.example.com:2222/org/repo.git"
                      autoFocus
                      disabled={isPending}
                    />
                    <p className="text-xs text-text-tertiary">
                      Use this for Mahoosuc/customer Gitea repositories that are not published as
                      internal baselines yet.
                    </p>
                  </TabsContent>

                  <TabsContent value="local-device" className="space-y-3">
                    <div className="rounded-md border border-border px-3 py-3 text-sm text-text-secondary">
                      Cloud Maestro cannot browse a workstation path until that device is enrolled.
                      Prepare the local-device handshake, approve secret release, then return here
                      and use the Path tab for the mounted checkout.
                    </div>
                    <Button asChild variant="outline" size="sm">
                      <Link to="/local-enrollment">Open local enrollment</Link>
                    </Button>
                    <p className="text-xs text-text-tertiary">
                      This prevents accidental server-local paths from being mistaken for your
                      laptop or WSL checkout.
                    </p>
                  </TabsContent>

                  <TabsContent value="path" className="space-y-3">
                    <label
                      htmlFor="project-server-path"
                      className="text-sm font-medium text-text-primary"
                    >
                      Server-mounted checkout path
                    </label>
                    <div className="flex gap-2">
                      <Input
                        id="project-server-path"
                        data-testid="add-project-path-input"
                        value={pathInput}
                        onChange={e => {
                          setPathInput(e.target.value);
                        }}
                        placeholder="/path/to/your/project"
                        autoFocus
                        disabled={isPending}
                        className="min-h-11 flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="min-h-11 min-w-11"
                        onClick={() => {
                          setStep({ kind: 'browse' });
                        }}
                        disabled={isPending}
                        data-testid="add-project-browse-button"
                        aria-label="Browse filesystem for a server-mounted checkout path"
                        title="Browse filesystem"
                      >
                        <FolderOpen className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                    <p className="text-xs text-text-tertiary">
                      Register an existing checkout already on the Maestro server or enrolled local
                      device without re-cloning it.
                    </p>
                  </TabsContent>
                </Tabs>
                {displayError && (
                  <div className="rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
                    {displayError.length > 200 ? `${displayError.slice(0, 200)}...` : displayError}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="min-h-11"
                    onClick={() => {
                      onOpenChange(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="min-h-11"
                    disabled={isPending || !canSubmit}
                    data-testid="add-project-submit"
                  >
                    {isPending ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </form>
            </>
          )}

          {step.kind === 'batch-confirm' && (
            <>
              <DialogHeader>
                <DialogTitle>Add {step.paths.length} Projects</DialogTitle>
                <DialogDescription>
                  The following repositories will be registered. They&apos;ll be processed one at a
                  time.
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {step.paths.map((p, i) => (
                  <div
                    key={p}
                    className="flex items-center justify-between rounded-md border border-border px-2 py-1.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-xs text-text-primary">
                        {p.split('/').pop()}
                      </div>
                      <div className="truncate text-[10px] text-text-tertiary">{p}</div>
                    </div>
                    <button
                      onClick={() => {
                        removeBatchPath(i);
                      }}
                      className="ml-2 p-0.5 text-text-tertiary hover:text-error"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              {error && (
                <div className="rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
                  {error}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStep({ kind: 'input' });
                  }}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    handleBatchSubmit(true);
                  }}
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    `Add All (${String(step.paths.length)})`
                  )}
                </Button>
              </div>
            </>
          )}

          {step.kind === 'key-review' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-warning" />
                  API Keys Detected
                </DialogTitle>
                <DialogDescription>
                  The following API keys were found in your project&apos;s .env files. Move them to
                  Maestro&apos;s encrypted vault for secure storage.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                {step.detectedKeys.map((dk, i) => (
                  <label
                    key={`${dk.file}-${dk.name}`}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-2 hover:bg-surface"
                  >
                    <input
                      type="checkbox"
                      checked={dk.moveToSecure}
                      onChange={() => {
                        toggleKey(i);
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                    <div className="min-w-0 flex-1">
                      <span className="font-mono text-sm text-text-primary">{dk.name}</span>
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        {dk.file}
                      </Badge>
                    </div>
                  </label>
                ))}
              </div>
              {error && (
                <div className="rounded-md border border-error/50 bg-error/10 p-2 text-sm text-error">
                  {error.length > 200 ? `${error.slice(0, 200)}...` : error}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSkipMigration}
                  disabled={isPending}
                >
                  Skip (allow keys in .env)
                </Button>
                <Button size="sm" onClick={handleMigrate} disabled={isPending}>
                  <ShieldCheck className="mr-1.5 h-4 w-4" />
                  {isPending ? 'Migrating...' : 'Move to Vault & Register'}
                </Button>
              </div>
            </>
          )}

          {step.kind === 'success' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/20">
                <Check className="h-6 w-6 text-success" />
              </div>
              <div className="text-center">
                <p className="font-medium text-text-primary">
                  {getProjectDisplayName({
                    name: step.codebase.name,
                    repository_url: step.codebase.repository_url,
                  })}
                </p>
                <p className="text-sm text-text-secondary">Project added successfully</p>
              </div>
              <RegistrationReceipt codebase={step.codebase} />
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => {
                    handleOpenConsume(step.codebase);
                  }}
                >
                  Consume into lifecycle
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    handleOpenWorkbench(step.codebase);
                  }}
                >
                  Open workbench
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
          )}

          {step.kind === 'batch-started' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
              <div className="text-center">
                <p className="font-medium text-text-primary">
                  Adding {String(step.total)} projects...
                </p>
                <p className="text-sm text-text-secondary">
                  Progress shown in the info panel below
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {step.kind === 'browse' && (
        <DirectoryBrowser
          open={true}
          onOpenChange={isOpen => {
            if (!isOpen) setStep({ kind: 'input' });
          }}
          onSelect={handleBrowseSelect}
        />
      )}

      {registeredCodebase && (
        <ConsumeProjectWizard
          codebaseId={consumeTarget?.id ?? registeredCodebase.id}
          codebaseName={
            consumeTarget?.name ??
            getProjectDisplayName({
              name: registeredCodebase.name,
              repository_url: registeredCodebase.repository_url,
            })
          }
          open={consumeWizardOpen}
          onOpenChange={setConsumeWizardOpen}
          onConsumed={() => {
            handleOpenWorkbench(registeredCodebase);
          }}
        />
      )}
      {!registeredCodebase && consumeTarget && (
        <ConsumeProjectWizard
          codebaseId={consumeTarget.id}
          codebaseName={consumeTarget.name}
          open={consumeWizardOpen}
          onOpenChange={setConsumeWizardOpen}
          onConsumed={() => {
            setSelectedProjectId(consumeTarget.id);
            refreshProjectReadinessQueries();
            void navigate('/workbench');
          }}
        />
      )}
    </>
  );
}
